import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Clock, HelpCircle } from 'lucide-react';

const policies = [
  {
    icon: CheckCircle,
    title: 'Full Refund',
    desc: '100% refund within 7 days of cohort start, no questions asked.',
    color: 'text-green-600',
    bg: 'bg-green-50',
  },
  {
    icon: Clock,
    title: 'Prorated Refund',
    desc: 'After Week 2, prorated refunds available for unforeseen circumstances.',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
  },
  {
    icon: XCircle,
    title: 'No Refund',
    desc: 'No refunds after 50% of the course is completed.',
    color: 'text-red-600',
    bg: 'bg-red-50',
  },
];

export default function RefundPolicyScreen(): React.JSX.Element {
  return (
    <div className="min-h-screen bg-white">
      <section className="pt-32 pb-16 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-6">
              Legal
            </span>
            <h1 className="text-4xl font-extrabold text-slate-900 mb-4">Refund Policy</h1>
            <p className="text-slate-500">We want you to be 100% satisfied with your investment</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Policy Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {policies.map((policy, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 text-center hover:shadow-lg transition-all"
              >
                <div className={`w-14 h-14 rounded-2xl ${policy.bg} flex items-center justify-center mx-auto mb-4`}>
                  <policy.icon className={`w-7 h-7 ${policy.color}`} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{policy.title}</h3>
                <p className="text-sm text-slate-600">{policy.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Details */}
          <div className="space-y-8">
            {[
              {
                title: '7-Day Money-Back Guarantee',
                content: 'We offer a full refund within 7 days of the cohort start date, no questions asked. Simply email us at refunds@ecoleminds.com with your enrollment ID, and we will process your refund within 5-7 business days.'
              },
              {
                title: 'Prorated Refunds (After Week 2)',
                content: 'If you need to drop out after Week 2 due to unforeseen circumstances (medical emergency, job loss, etc.), we offer prorated refunds based on the remaining course content. Documentation may be required. Contact our support team to discuss your situation.'
              },
              {
                title: 'Non-Refundable Cases',
                content: 'Refunds are not available if: (1) You have completed more than 50% of the course, (2) You have already received your certificate, (3) The refund request is made after the cohort end date, (4) The request is due to a change of mind after significant course progress.'
              },
              {
                title: 'Refund Process',
                content: 'To request a refund, email refunds@ecoleminds.com with your registered email, enrollment ID, and reason for refund. Refunds are processed to the original payment method within 5-7 business days. Razorpay processing fees (2%) are non-refundable.'
              },
              {
                title: 'Course Transfer',
                content: 'Instead of a refund, you may transfer your enrollment to a future cohort at no additional cost. This must be requested before Week 3 of the current cohort.'
              },
            ].map((section, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <h2 className="text-xl font-bold text-slate-900 mb-3">{section.title}</h2>
                <p className="text-slate-600 leading-relaxed">{section.content}</p>
              </motion.div>
            ))}
          </div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12 p-6 bg-amber-50 rounded-2xl border border-amber-100"
          >
            <div className="flex items-start gap-4">
              <HelpCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-slate-900 mb-1">Questions about refunds?</h3>
                <p className="text-sm text-slate-600">
                  Contact our support team at{' '}
                  <a href="mailto:refunds@ecoleminds.com" className="text-amber-500 font-semibold hover:underline">
                    refunds@ecoleminds.com
                  </a>{' '}
                  or WhatsApp us at +91 98765 43210.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
